import {useMemo, useEffect, Fragment} from 'react';

import styled, {css} from 'styled-components';

import {attachModal} from '~/shared/components/ReduxModal';
import {getLocalizationService} from '~/shared/services/localisationService';
import {media} from '~/shared/theme/utils';
import {flexCenterVertically} from '~/shared/theme/FlexLayout';
import {bodyRegular, headingS, headingXS, smallBold, smallRegular} from '~/shared/theme/typography';
import {trackEvent} from '~/shared/services/analytics';
import ClickOutsideDiv from '~/shared/components/ClickOutsideDiv';
import ManagerProvider from '~/shared/managers/ManagerProvider';
import PieButton from '~/shared/theme/pie/PieButton';
import {isDeliveryRuleModel} from '~/shared/utils/restaurant';
import {DELIVERY_RULES} from '~/shared/consts/restaurantConsts';
import ImageWithAlt from '~/shared/components/ImageWithAlt';
import {PieModalHeader} from '~/shared/components/Modals/styled';
import {Dish as DishType} from '~/shared/store/models';

import modalImg from './reorderModalImg.png';

const Root = styled(ClickOutsideDiv)`
  color: ${({theme}) => theme.colors.contentDefault};

  &&& {
    background-color: ${({theme}) => theme.colors.surface};
    max-height: 100%;
    height: 100%;
  }

  border-radius: 2px;
  box-shadow: ${({theme}) => theme.shadows.shadow1};
  ${media.minLargeMobile`
    &&& {
        width: 500px;
        height: auto;
    }
    text-align: center;     
  `}
`;

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  padding: 8px 16px 40px;
  justify-content: center;
  align-items: center;
  ${media.minLargeMobile`
    padding: 32px 32px 24px;
  `}
`;

const NoDishesImageWithTextsContainer = styled(ContentWrapper)`
  padding: 0 8px;
  ${media.minLargeMobile`
    padding: 0;
  `}
`;

const Image = styled(ImageWithAlt).attrs({
  src: modalImg,
  alt: 'Reorder modal image',
})`
  width: 150px;
  height: 150px;
  ${media.minLargeMobile`
    width: 180px;
    height: 180px;
  `}
`;

const DialogHeaderText = styled.h3`
  ${headingXS}
  width: 100%;
  text-align: center;
  margin-top: 24px;
  ${media.minLargeMobile`
    ${headingS}
  `}
`;

const ReorderUnavailableText = styled.div`
  ${bodyRegular}
  margin-top: 8px;
  text-align: center;
  width: 100%;
`;

const Dishes = styled.div`
  margin-top: 32px;
  width: 100%;
  max-height: 270px;
  ${media.minLargeTablet`
    max-height: 280px;
  `}
`;

const Buttons = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  justify-content: center;
  margin-top: 32px;
  width: 100%;
  ${media.minLargeMobile`
    flex-direction: row;
  `}
`;

const StyledPieButton = styled(PieButton)`
  padding: 10px 16px;
  ${media.minLargeMobile`
    padding: 12px 16px;
  `}
`;

const CancelReorderButton = styled(StyledPieButton)`
  width: 100%;
`;

const ContinueButton = styled(StyledPieButton)`
  width: 100%;
`;

const Dish = styled.div`
  ${flexCenterVertically}
  justify-content: space-between;
  color: ${({theme}) => theme.colors.contentDefault};
  width: 100%;
  padding: 16px 0;
`;

const textsBaseStyle = css<{crossed?: boolean}>`
  text-decoration: ${({crossed}) => (crossed === true ? 'line-through' : 'none')};
  display: flex;
  padding: 0;
`;

const DishQuantity = styled.span`
  ${smallBold}
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background-color: ${({theme}) => theme.colors.backgroundSubtle};
  display: flex;
  justify-content: center;
  align-items: center;
`;

const DishNameAndQuantityWrapper = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`;

const DishName = styled.span`
  ${textsBaseStyle}
  ${smallRegular}
  margin-right: 8px;
`;

const DishMissingLabel = styled.span`
  ${smallRegular}
  display: flex;
  padding: 1px 6px;
  border-radius: 4px;
  background-color: ${({theme}) => theme.colors.feedbackError};
  color: ${({theme}) => theme.colors.contentError};
`;

const DishLine = styled.div`
  height: 1px;
  border: solid 1px rgba(0, 0, 0, 0.08);
`;

const MENU_ITEMS_TYPE = {
  DISH: 'Dish',
  SUB: 'Sub',
};

type RestaurantReorderModalProps = {
  closeModal: () => void;
  args: {
    isMinLargeMobile?: boolean;
    allMenuItems: Array<{
      id: number;
      name: string;
      quantity: number;
      unavailable?: boolean;
      price?: number;
    }>;
    unavailableMenuItems: Array<{
      menuItemId: number;
      menuItemType: typeof MENU_ITEMS_TYPE[keyof typeof MENU_ITEMS_TYPE];
      dishAncestorId?: number;
    }>;
    currentRestaurantsMenuDishesMap: Record<number, DishType>;
    orderId: number;
    currentDeliveryRule: DELIVERY_RULES | null;
    onCancelClick?: () => void;
    onCloseClick?: () => void;
  };
};

const RestaurantReorderModal = ({closeModal, args}: RestaurantReorderModalProps) => {
  const {t} = getLocalizationService();

  const {
    isMinLargeMobile,
    allMenuItems,
    unavailableMenuItems,
    currentRestaurantsMenuDishesMap,
    orderId,
    currentDeliveryRule,
    onCancelClick,
    onCloseClick,
  } = args;

  const primaryButtonVariant = isMinLargeMobile ? 'primaryXL' : 'primaryLarge';
  const secondaryButtonVariant = isMinLargeMobile ? 'secondaryXL' : 'secondaryLarge';

  const isDishesExists = Boolean(
    allMenuItems?.length && unavailableMenuItems?.length && allMenuItems.length > unavailableMenuItems.length,
  );

  const menuItems = useMemo(
    () =>
      allMenuItems.map(item => {
        if (
          unavailableMenuItems.find(
            unItem =>
              (unItem.menuItemType === MENU_ITEMS_TYPE.DISH && unItem.menuItemId === item.id) ||
              (unItem.menuItemType === MENU_ITEMS_TYPE.SUB && unItem.dishAncestorId === item.id),
          )
        ) {
          return {...item, unavailable: true};
        }
        return {...item, price: currentRestaurantsMenuDishesMap[item.id]?.price};
      }),
    [allMenuItems, unavailableMenuItems, currentRestaurantsMenuDishesMap],
  );

  const onCancelClickHandler = () => {
    trackEvent('hasReorderPopupUnavailable', {
      linkType: 'Cancel reorder',
    });
    closeModal();
    if (onCancelClick) {
      onCancelClick();
    }
  };

  const onCloseClickHandler = () => {
    if (isDishesExists) {
      trackEvent('hasReorderPopupUnavailable', {
        linkType: 'Close',
      });
    }
    closeModal();
    if (onCloseClick) {
      onCloseClick();
    }
  };

  useEffect(() => {
    if (!isDishesExists) {
      trackEvent('hasReorderPopupAllUnavailable');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onContinueClickHandler = () => {
    trackEvent('hasReorderPopupUnavailable', {
      linkType: 'Continue without dishes',
    });
    ManagerProvider.reorder(orderId, {
      addDishesAnyway: true,
      deliveryRuleType: isDeliveryRuleModel(currentDeliveryRule) ? currentDeliveryRule.type : undefined,
    });
    closeModal();
  };

  const ImageAndTextsContainer = isDishesExists ? Fragment : NoDishesImageWithTextsContainer;

  return (
    <Root onClickOutside={onCloseClickHandler}>
      <PieModalHeader hideOnMobile />
      <ContentWrapper>
        <ImageAndTextsContainer>
          <Image />

          <DialogHeaderText id="modal-title">
            {isDishesExists ? t('looks_like_few_things_are_missing') : t('no_option_to_reorder')}
          </DialogHeaderText>

          {!isDishesExists && (
            <ReorderUnavailableText>
              {t('items_in_the_order_are_not_available')}
            </ReorderUnavailableText>
          )}

          {isDishesExists && (
            <Dishes>
              {menuItems?.length > 0 && menuItems.map((dish, index) => (
                <Fragment key={dish.id}>
                  <Dish>
                    <DishNameAndQuantityWrapper>
                      <DishQuantity>{dish.quantity}</DishQuantity>
                      <DishName crossed={dish.unavailable}>{dish.name}</DishName>
                    </DishNameAndQuantityWrapper>
                    {dish.unavailable && <DishMissingLabel>{t('out_of_stock')}</DishMissingLabel>}
                  </Dish>
                  {index < menuItems.length - 1 && <DishLine />}
                </Fragment>
              ))}
            </Dishes>
          )}
        </ImageAndTextsContainer>

        {isDishesExists ? (
          <Buttons>
            <ContinueButton variant={primaryButtonVariant} onClick={onContinueClickHandler}>{t('continue_without_dishes')}</ContinueButton>
            <CancelReorderButton variant={secondaryButtonVariant} onClick={onCancelClickHandler}>{t('back_to_menu')}</CancelReorderButton>
          </Buttons>
        ) : (
          <Buttons>
            <ContinueButton variant={primaryButtonVariant} onClick={onCloseClickHandler}>{t('accept')}</ContinueButton>
          </Buttons>
        )}
      </ContentWrapper>
    </Root>
  );
};

RestaurantReorderModal.modalType = 'autoWidth';
RestaurantReorderModal.mobileBottomSheet = true;

attachModal('restaurantReorderModal', RestaurantReorderModal);

export default RestaurantReorderModal;
